// Service de notification sans dépendance directe à Firebase Messaging
// Cela évite les erreurs sur les navigateurs non compatibles
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../config/firebase.config';

// Interface pour les notifications
interface NotificationPayload {
  notification?: {
    title?: string;
    body?: string;
  };
  data?: any;
}

/**
 * Vérifie si le navigateur est compatible avec les notifications
 */
export const isNotificationsSupported = (): boolean => {
  try {
    // Vérifier si nous sommes dans un navigateur
    if (typeof window === 'undefined') return false;
    
    // Vérifier si les notifications sont prises en charge
    if (!('Notification' in window)) return false;
    
    return true;
  } catch (error) {
    console.error('Erreur lors de la vérification de compatibilité:', error);
    return false;
  }
};

/**
 * Demande la permission d'envoyer des notifications
 * Version simplifiée sans Firebase Messaging
 */
export const requestNotificationPermission = async (): Promise<string | null> => {
  // Vérifier d'abord si le navigateur est compatible avec les notifications
  if (!isNotificationsSupported()) {
    console.log('⚠️ Ce navigateur ne prend pas en charge les notifications');
    return null;
  }
  
  try {
    // Demander la permission avec un timeout pour éviter les blocages
    const permissionPromise = Notification.requestPermission();
    const timeoutPromise = new Promise<string>((resolve) => {
      setTimeout(() => resolve('timeout'), 3000);
    });
    
    const permission = await Promise.race([permissionPromise, timeoutPromise]);
    
    if (permission === 'timeout') {
      console.log('⚠️ Timeout lors de la demande de permission, on continue sans notifications');
      return null;
    }
    
    if (permission === 'granted') {
      // Générer un ID unique pour identifier cet appareil
      // Cela remplace le token FCM pour les navigateurs non compatibles
      const deviceId = generateDeviceId();
      console.log('✅ Permission de notification accordée, ID généré:', deviceId);
      return deviceId;
    } else {
      console.log('⚠️ Permission de notification refusée');
      return null;
    }
  } catch (error) {
    console.error('⚠️ Erreur lors de la demande de permission de notification:', error);
    return null;
  }
};

/**
 * Génère un ID unique pour identifier cet appareil
 * Remplace le token FCM pour les navigateurs non compatibles
 */
const generateDeviceId = (): string => {
  // Générer un ID unique basé sur un timestamp et un nombre aléatoire
  const timestamp = new Date().getTime();
  const random = Math.floor(Math.random() * 1000000);
  return `device_${timestamp}_${random}`;
};

/**
 * Enregistre l'ID de l'appareil dans Firestore pour l'utilisateur connecté
 * @param userId ID de l'utilisateur
 * @param deviceId ID de l'appareil
 */
export const saveTokenToFirestore = async (userId: string, deviceId: string): Promise<void> => {
  if (!deviceId) return;
  
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      // Utiliser le même champ que pour les tokens FCM pour compatibilité
      fcmTokens: arrayUnion(deviceId)
    });
    console.log('✅ ID de l\'appareil enregistré dans Firestore');
  } catch (error) {
    console.error('❌ Erreur lors de l\'enregistrement de l\'ID de l\'appareil:', error);
  }
};

/**
 * Écoute les messages entrants lorsque l'application est au premier plan
 * Version simplifiée qui ne fait rien sur les navigateurs non compatibles
 * @param callback Fonction à appeler lorsqu'un message est reçu
 */
export const onForegroundMessage = (callback: (payload: NotificationPayload) => void): (() => void) => {
  // Retourner une fonction vide qui ne fait rien
  // Les notifications seront gérées par le service worker si disponible
  return () => {};
};

// Exporter l'interface publique du service
export const NotificationService = {
  requestPermission: requestNotificationPermission,
  saveToken: saveTokenToFirestore,
  onForegroundMessage
};
